// frontend/src/components/AdminPlayerDirectory.js
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { adminService, supabase } from '../services/supabaseClient';
import FlagDisplay from './FlagDisplay';
import { toast } from 'react-hot-toast';
import AdminHeader from './AdminHeader';

const AdminPlayerDirectory = () => {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const navigate = useNavigate();

    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [teamFilter, setTeamFilter] = useState('all');
    const [allTeams, setAllTeams] = useState([]);
    const [globalStats, setGlobalStats] = useState({ total: null, linked: null, withPhoto: null });

    // Fetch all unique teams from database on mount
    useEffect(() => {
        const fetchAllTeams = async () => {
            const { data, error } = await supabase
                .from('event_uploads')
                .select('team')
                .not('team', 'is', null);

            if (!error && data) {
                const uniqueTeams = [...new Set(data.map(d => d.team))].sort();
                setAllTeams(uniqueTeams);
            }
        };
        fetchAllTeams();
    }, []);

    // Fetch global counts on mount (head:true → cheap COUNT, no rows fetched)
    useEffect(() => {
        const fetchGlobalStats = async () => {
            try {
                const [totalRes, linkedRes, withPhotoRes] = await Promise.all([
                    supabase.from('event_uploads').select('*', { count: 'exact', head: true }),
                    supabase.from('event_uploads').select('*', { count: 'exact', head: true }).not('person_id', 'is', null),
                    supabase.from('event_uploads').select('*', { count: 'exact', head: true }).eq('upload_status', true)
                ]);
                setGlobalStats({
                    total: totalRes.count ?? 0,
                    linked: linkedRes.count ?? 0,
                    withPhoto: withPhotoRes.count ?? 0
                });
            } catch (err) {
                // Silent fail — strip just stays hidden
                console.error('Global stats fetch failed:', err);
            }
        };
        fetchGlobalStats();
    }, []);


    const fetchPlayers = useCallback(async (offset = 0, isReset = false, team = 'all') => {
        setLoading(true);
        try {
            let data = [];
            let error = null;

            if (query) {
                // Search Mode
                const result = await adminService.globalSearch(query);
                data = result.data || [];
                error = result.error;
                // Apply team filter to search results
                if (team !== 'all') {
                    data = data.filter(p => p.team === team);
                }
                setHasMore(false);
            } else if (team !== 'all') {
                // Team Filter Mode - fetch players by team
                const { data: teamData, error: teamError } = await supabase
                    .from('event_uploads')
                    .select('id, player_name, team, upload_status, photo_url, event_name, created_at')
                    .eq('team', team)
                    .order('created_at', { ascending: false });

                // Get event codes for navigation
                const eventNames = [...new Set((teamData || []).map(p => p.event_name))];
                let eventCodeMap = {};
                if (eventNames.length > 0) {
                    const { data: eventsData } = await supabase
                        .from('events')
                        .select('short_name, code')
                        .in('short_name', eventNames);
                    (eventsData || []).forEach(e => {
                        eventCodeMap[e.short_name] = e.code;
                    });
                }

                data = (teamData || []).map(p => ({
                    ...p,
                    event_code: eventCodeMap[p.event_name] || null
                }));
                error = teamError;
                setHasMore(false);
            } else {
                // Feed Mode (Recent Uploads)
                const limit = 20;
                const result = await adminService.getRecentPlayers(limit, offset);
                data = result.data || [];
                error = result.error;

                if (data.length < limit) {
                    setHasMore(false);
                }
            }

            if (error) throw error;

            setPlayers(prev => isReset ? data : [...prev, ...data]);
            setPage(prev => prev + 1);

        } catch (err) {
            console.error('Error fetching players:', err);
            toast.error('Failed to load players');
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => {
        // Reset when query or team filter changes
        setPlayers([]);
        setPage(0);
        setHasMore(true);
        fetchPlayers(0, true, teamFilter);
    }, [query, teamFilter, fetchPlayers]);

    const loadMore = () => {
        if (!loading && hasMore && !query && teamFilter === 'all') {
            fetchPlayers(page * 20, false, teamFilter);
        }
    };

    const handleCardClick = (player) => {
        if (player.event_code) {
            const urlSafeTeam = encodeURIComponent(player.team);
            navigate(`/admin/${player.event_code}/${urlSafeTeam}`);
        } else if (player.event_name) {
            console.warn('No event code for navigation', player);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <AdminHeader currentPage="players" />

            {/* Page Heading */}
            <div className="bg-mjwc-primary relative overflow-hidden">
                <div className="absolute inset-0 z-0 bg-gradient-to-br from-mjwc-primary via-mjwc-secondary to-indigo-900" />
                <div className="relative z-20 max-w-7xl mx-auto px-6 py-10 sm:py-12">
                    <div className="mb-4">
                        <h1 className="text-4xl font-black text-white tracking-tight leading-none mb-2">
                            {query ? `Search: "${query}"` : 'Global Player Directory'}
                        </h1>
                        <p className="text-white/60 font-medium">
                            {query ? 'Players matching across all managed events' : 'A complete timeline of recent player additions across all events'}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-8">
                        {/* Search & Filter Controls Moved Here */}
                        <div className="flex flex-wrap items-center gap-3 w-full max-w-4xl">
                            <div className="relative flex-1 min-w-[300px]">
                                <input
                                    type="text"
                                    defaultValue={query}
                                    placeholder="Search by player name..."
                                    className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:bg-white/20 focus:border-white/40 transition-all font-medium"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            navigate(`/admin/players?q=${encodeURIComponent(e.target.value)}`);
                                        }
                                    }}
                                />
                                <svg className="w-5 h-5 text-white/40 absolute left-4 top-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>

                            <select
                                value={teamFilter}
                                onChange={(e) => setTeamFilter(e.target.value)}
                                className="px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl text-white/90 focus:outline-none focus:bg-white/20 font-bold text-sm min-w-[200px]"
                            >
                                <option value="all" className="bg-mjwc-primary text-white">All Teams</option>
                                {allTeams.map(team => (
                                    <option key={team} value={team} className="bg-mjwc-primary text-white">{team}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                {/* Wave effect */}
                <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none transform rotate-180 translate-y-[1px]">
                    <svg className="relative block w-full h-[40px] fill-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
                        <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
                    </svg>
                </div>
            </div>


            {/* Main Content */}
            <div className="px-6 py-8">
                {/* Top stats strip — shown once counts have loaded */}
                {globalStats.total !== null && (
                    <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 max-w-3xl">
                        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Total Players</div>
                            <div className="text-2xl font-black text-gray-900 mt-0.5">{globalStats.total.toLocaleString()}</div>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Linked to Sportradar</div>
                            <div className="text-2xl font-black text-blue-600 mt-0.5">
                                {globalStats.linked.toLocaleString()}
                                {globalStats.total > 0 && (
                                    <span className="text-xs font-semibold text-gray-400 ml-1.5">
                                        {Math.round((globalStats.linked / globalStats.total) * 100)}%
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">With Photos</div>
                            <div className="text-2xl font-black text-emerald-600 mt-0.5">
                                {globalStats.withPhoto.toLocaleString()}
                                {globalStats.total > 0 && (
                                    <span className="text-xs font-semibold text-gray-400 ml-1.5">
                                        {Math.round((globalStats.withPhoto / globalStats.total) * 100)}%
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {players.map((player) => (
                        <div
                            key={`${player.id}-${player.event_name}`}
                            className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                            onClick={() => handleCardClick(player)}
                        >
                            {/* Header / Event Context */}
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider truncate max-w-[70%]">
                                    {player.event_name}
                                </span>
                                <span className="text-xs text-gray-400">
                                    {new Date(player.created_at).toLocaleDateString()}
                                </span>
                            </div>

                            {/* Body */}
                            <div className="p-4 flex items-start gap-4 flex-1">
                                {/* Avatar */}
                                <div className="relative shrink-0">
                                    <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-white shadow-sm overflow-hidden">
                                        {player.uploaded_photo_url || player.photo_url ? (
                                            <img
                                                src={player.uploaded_photo_url || player.photo_url}
                                                alt={player.player_name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400 font-bold text-xl">
                                                {player.player_name?.substring(0, 2).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    {/* Linked Badge */}
                                    {player.person_id && (
                                        <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white p-1 rounded-full border-2 border-white" title="Linked to Sportradar">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                        {player.player_name}
                                    </h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        <FlagDisplay teamName={player.team} size="sm" />
                                        <p className="text-sm text-gray-600 truncate">{player.team}</p>
                                    </div>
                                    {player.person_id && (
                                        <p className="text-xs text-blue-600 mt-2 bg-blue-50 inline-block px-2 py-0.5 rounded">
                                            ID: {player.person_id}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Context Footer (For Search Results) */}
                            {query && player.sml && (
                                <div className="px-4 py-2 bg-yellow-50 text-xs text-yellow-700 border-t border-yellow-100">
                                    Match confidence: {Math.round(player.sml * 100)}%
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="py-12 flex justify-center">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                )}

                {/* Empty State */}
                {!loading && players.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300 shadow-sm mt-8">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No players found</h3>
                        <p className="mt-1 text-sm text-gray-500">{query ? 'Try adjusting your search terms.' : 'No uploads found in the database.'}</p>
                        {query && (
                            <button
                                onClick={() => { navigate('/admin/players'); }}
                                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Clear Search
                            </button>
                        )}
                    </div>
                )}

                {/* Load More Trigger */}
                {!query && hasMore && !loading && (
                    <div className="mt-12 text-center pb-12">
                        <button
                            onClick={loadMore}
                            className="px-6 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                        >
                            Load More
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPlayerDirectory;
